/**
 * Determines if any user requires admin action for Regtank reattempt approval
 * Checks both onboarding and liveness verification status
 */

import { UserData } from "../types/org.type";
import {
  RegtankReattemptApprovalEnum,
  RegtankOnboardingFHStatusEnum,
  RegtankLivenessFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import {
  changeRegtankStatusAttemptUsed,
  changeRegtankLivenessData,
} from "../Utils/getRegtankDetails";
import { sortByExpiryDateDesc, toISOString } from "../Utils/regtankUtils";

/**
 * Checks if user's Regtank onboarding requires review
 */
const requiresOnboardingReview = (user: UserData): boolean => {
  if (!user.regtankOnboarding || !user.regtankRequest?.length) {
    return false;
  }

  const sortedRequests = sortByExpiryDateDesc(user.regtankRequest);
  const mostRecent = sortedRequests[0];
  const expiredDate = toISOString(mostRecent.expiredDate);

  const result = changeRegtankStatusAttemptUsed(
    mostRecent.requestId,
    mostRecent.onboardingStatus,
    expiredDate,
    mostRecent.reattemptApproval,
    user.regtankRequest.length - 1
  );

  return result.newStatus === RegtankOnboardingFHStatusEnum.PENDING_REVIEW;
};

/**
 * Checks if user's Regtank liveness verification requires review
 */
const requiresLivenessReview = (user: UserData): boolean => {
  if (!user.regtankLiveness?.length) {
    return false;
  }

  const sortedLiveness = sortByExpiryDateDesc(user.regtankLiveness);
  const mostRecent = sortedLiveness[0];
  const expiredDate = toISOString(mostRecent.expiredDate);

  const result = changeRegtankLivenessData(
    mostRecent.sessionId,
    mostRecent.livenessStatus,
    expiredDate,
    user.regtankLiveness.length - 1,
    mostRecent.reattemptApproval,
    expiredDate,
    mostRecent.reason
  );

  return result.newStatus === RegtankLivenessFHStatusEnum.PENDING_REVIEW;
};

/**
 * Determines if any user requires reattempt approval action
 * @returns ACTION_REQUIRED if any user needs review, NO_ACTION_REQUIRED otherwise
 */
export const getRegtankReattemptStatus = (users: UserData[]) => {
  if (!users?.length) {
    return RegtankReattemptApprovalEnum.NO_ACTION_REQUIRED;
  }

  // Check if any user requires review (onboarding or liveness)
  const hasActionRequired = users.some(
    (user) => requiresOnboardingReview(user) || requiresLivenessReview(user)
  );

  return hasActionRequired
    ? RegtankReattemptApprovalEnum.ACTION_REQUIRED
    : RegtankReattemptApprovalEnum.NO_ACTION_REQUIRED;
};
